package com.bulut.mecitogullari.ui.auth

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import com.bulut.mecitogullari.core.firebase.FirebaseConfig
import dev.gitlive.firebase.auth.externals.GoogleAuthProvider
import dev.gitlive.firebase.auth.externals.getAuth
import dev.gitlive.firebase.auth.externals.signInWithPopup
import kotlinx.coroutines.await
import kotlinx.coroutines.launch

/**
 * Web flow, in preference order:
 *
 * 1. Google Identity Services token client → Firebase signInWithCredential.
 *    This never touches the firebaseapp.com auth helper, so it keeps working
 *    on mobile Safari/Chrome where partitioned storage breaks
 *    signInWithPopup/Redirect ("missing initial state").
 * 2. Fallback: the classic Firebase popup (e.g. if the GIS script is blocked).
 */
@Composable
actual fun GoogleSignInContainer(
    onResult: (GoogleSignInResult) -> Unit,
    content: @Composable (onClick: () -> Unit) -> Unit,
) {
    val scope = rememberCoroutineScope()
    content {
        if (isGisAvailable()) {
            requestGisAccessToken(onResult)
        } else {
            scope.launch { firebasePopupSignIn(onResult) }
        }
    }
}

private fun isGisAvailable(): Boolean =
    js("typeof google !== 'undefined' && !!(google.accounts && google.accounts.oauth2)") as Boolean

private fun requestGisAccessToken(onResult: (GoogleSignInResult) -> Unit) {
    val config = js("{}")
    config.client_id = FirebaseConfig.WEB_CLIENT_ID
    config.scope = "openid email profile"
    config.callback = { response: dynamic ->
        val accessToken = response.access_token as? String
        if (accessToken != null) {
            onResult(GoogleSignInResult.Token(idToken = null, accessToken = accessToken))
        } else {
            onResult(GoogleSignInResult.Failed("Google oturum bilgisi alınamadı."))
        }
    }
    config.error_callback = { error: dynamic ->
        val type = (error?.type as? String).orEmpty()
        if (type == "popup_closed" || type == "user_cancel") {
            onResult(GoogleSignInResult.Cancelled)
        } else {
            onResult(GoogleSignInResult.Failed(error?.message as? String ?: type))
        }
    }
    val client = js("google.accounts.oauth2.initTokenClient")(config)
    client.requestAccessToken()
}

private suspend fun firebasePopupSignIn(onResult: (GoogleSignInResult) -> Unit) {
    try {
        signInWithPopup(getAuth(), GoogleAuthProvider()).await()
        onResult(GoogleSignInResult.SignedInDirectly)
    } catch (e: Throwable) {
        val message = e.message ?: ""
        if (message.contains("popup-closed", ignoreCase = true) ||
            message.contains("cancelled", ignoreCase = true)
        ) {
            onResult(GoogleSignInResult.Cancelled)
        } else {
            onResult(GoogleSignInResult.Failed(e.message))
        }
    }
}
